import React, { useEffect } from 'react';
import { Badge, Table } from 'react-bootstrap';
import { useAudit } from './AuditContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFile,
  faFileAlt,
  faCheckCircle,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { auditService } from './audit-service';

const AuditInfoPanel: React.FC = () => {
  const {
    selectedClient,
    pendingDocuments,
    setPendingDocuments,
    sentDocuments,
    setSentDocuments
  } = useAudit();

  // Load documents when selected client changes
  useEffect(() => {
    if (selectedClient) {
      setPendingDocuments(auditService.getPendingDocuments(selectedClient.id));
      setSentDocuments(auditService.getSentDocuments(selectedClient.id));
    }
  }, [selectedClient, setPendingDocuments, setSentDocuments]);

  // Format date for display
  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'Fecha no disponible';

    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'Fecha inválida';
    }
  };

  return (
    <div className="h-100 fs-9">
      <h5 className="mb-3">Información de Documentación</h5>

      {selectedClient ? (
        <div className="d-flex flex-column gap-4">
          {/* PENDING DOCUMENTS SECTION */}
          <div className="border rounded p-0">
            <div className="bg-body p-2 rounded">
              <div className="d-flex justify-content-start align-items-center">
                <h5 className="mb-0">
                  <FontAwesomeIcon
                    icon={faExclamationCircle}
                    className="me-2 text-warning"
                  />
                  Documentos Pendientes
                </h5>
                <Badge bg="warning" className="mx-3">
                  {pendingDocuments.length}
                </Badge>
              </div>
            </div>
            <div className="p-0">
              <div
                style={{ height: '271px', overflowY: 'auto' }}
                className="rounded bg-body-emphasis scrollbar px-3"
              >
                {pendingDocuments.length === 0 ? (
                  <div className="m-2 bg-body border rounded p-4 fs-9">
                    No hay documentos pendientes para este cliente.
                  </div>
                ) : (
                  <Table hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Descripción</th>
                        <th>Fecha límite</th>
                        <th>Enviar a</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingDocuments.map(doc => (
                        <tr key={doc.id}>
                          <td className="fs-10 px-1">
                            <FontAwesomeIcon icon={faFile} className="me-2" />
                            {doc.documentType}
                          </td>
                          <td className="fs-10">{doc.description}</td>
                          <td className="fs-10">{formatDate(doc.deadline)}</td>
                          <td className="fs-10">{doc.emailTo.join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </div>

          {/* SENT DOCUMENTS SECTION */}
          <div className="border rounded p-0">
            <div className="bg-body p-2 rounded">
              <div className="d-flex justify-content-start align-items-center">
                <h5 className="mb-0">
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="me-2 text-success"
                  />
                  Documentos Enviados
                </h5>
                <Badge bg="success" className="mx-3">
                  {sentDocuments.length}
                </Badge>
              </div>
            </div>
            <div className="p-0">
              <div
                style={{ height: '270px', overflowY: 'auto' }}
                className="rounded bg-body-emphasis scrollbar px-3"
              >
                {sentDocuments.length === 0 ? (
                  <div className="m-2 bg-body border rounded p-4 fs-9">
                    No hay documentos enviados registrados para este cliente.
                  </div>
                ) : (
                  <Table hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Descripción</th>
                        <th>Fecha envío</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sentDocuments.map(doc => (
                        <tr key={doc.id}>
                          <td className="fs-10">
                            <FontAwesomeIcon
                              icon={faFileAlt}
                              className="me-2"
                            />
                            {doc.documentType}
                          </td>
                          <td className="fs-10">{doc.description}</td>
                          <td className="fs-10">
                            {formatDate(doc.sentDate || '')}
                          </td>
                          <td>
                            <Badge bg="success">
                              <FontAwesomeIcon
                                icon={faCheckCircle}
                                className="me-1"
                              />
                              Enviado
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="m-2 bg-body border rounded p-4 fs-9">
          Seleccione un cliente para ver su documentación.
        </div>
      )}
    </div>
  );
};

export default AuditInfoPanel;
